import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { 
  Clock, Play, Pause, Trash2, Plus, RefreshCw, 
  Mail, Calendar, Settings2, Zap, CheckCircle2, AlertCircle
} from 'lucide-react';

interface ScheduledFollowUp {
  id: string;
  backlink_id: string;
  website_name: string;
  contact_email: string;
  follow_up_number: number;
  scheduled_for: string;
  status: 'pending' | 'sent' | 'cancelled';
  template_type: string;
}

interface AutomationRule {
  id: string;
  name: string;
  enabled: boolean;
  followUpInterval: number; // days
  maxFollowUps: number;
  templateSequence: string[];
}

interface ScheduledOutreachManagerProps {
  userId: string;
}

export const ScheduledOutreachManager = ({ userId }: ScheduledOutreachManagerProps) => {
  const [scheduledEmails, setScheduledEmails] = useState<ScheduledFollowUp[]>([]);
  const [loading, setLoading] = useState(true);
  const [automationRules, setAutomationRules] = useState<AutomationRule[]>([
    {
      id: '1',
      name: 'Default Follow-up Sequence',
      enabled: true,
      followUpInterval: 3,
      maxFollowUps: 3,
      templateSequence: ['initial', 'follow_up_1', 'follow_up_2'],
    }
  ]);
  const [newRule, setNewRule] = useState({
    name: '',
    followUpInterval: 3,
    maxFollowUps: 3,
  });

  useEffect(() => {
    fetchScheduledEmails();
    checkAndScheduleFollowUps();
  }, [userId]);

  const fetchScheduledEmails = async () => {
    try {
      // Get backlinks that need follow-ups
      const { data: backlinks, error } = await supabase
        .from('backlinks')
        .select('*')
        .eq('agent_id', userId)
        .eq('outreach_status', 'contacted')
        .not('next_follow_up_date', 'is', null)
        .order('next_follow_up_date', { ascending: true });

      if (error) throw error;

      const scheduled: ScheduledFollowUp[] = (backlinks || []).map(b => ({
        id: `followup_${b.id}`,
        backlink_id: b.id,
        website_name: b.website_name,
        contact_email: b.contact_email || '',
        follow_up_number: (b.follow_up_count || 0) + 1,
        scheduled_for: b.next_follow_up_date || '',
        status: 'pending',
        template_type: `follow_up_${(b.follow_up_count || 0) + 1}`,
      }));

      setScheduledEmails(scheduled);
    } catch (error) {
      console.error('Error fetching scheduled emails:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkAndScheduleFollowUps = async () => {
    try {
      // Get backlinks that were contacted but don't have follow-up scheduled
      const { data: backlinks, error } = await supabase
        .from('backlinks')
        .select('*')
        .eq('agent_id', userId)
        .eq('outreach_status', 'contacted')
        .is('next_follow_up_date', null)
        .lt('follow_up_count', automationRules[0].maxFollowUps);

      if (error) throw error;

      // Schedule follow-ups for each
      for (const backlink of backlinks || []) {
        const lastOutreach = new Date(backlink.last_outreach_date || backlink.created_at);
        const nextFollowUp = new Date(lastOutreach);
        nextFollowUp.setDate(nextFollowUp.getDate() + automationRules[0].followUpInterval);

        await supabase
          .from('backlinks')
          .update({ 
            next_follow_up_date: nextFollowUp.toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('id', backlink.id);
      }

      if ((backlinks?.length || 0) > 0) {
        fetchScheduledEmails();
        toast.success(`Scheduled ${backlinks?.length} follow-up emails`);
      }
    } catch (error) {
      console.error('Error scheduling follow-ups:', error);
    }
  };

  const sendFollowUpNow = async (email: ScheduledFollowUp) => {
    try {
      // Get backlink details
      const { data: backlink, error: fetchError } = await supabase
        .from('backlinks')
        .select('*')
        .eq('id', email.backlink_id)
        .single();

      if (fetchError || !backlink) throw fetchError || new Error('Backlink not found');

      // Generate follow-up content
      const followUpNumber = email.follow_up_number;
      const subject = `Re: Partnership Opportunity - ${backlink.website_name}`;
      const body = generateFollowUpBody(backlink, followUpNumber);

      // Send email
      const { error: sendError } = await supabase.functions.invoke('send-email-template', {
        body: {
          to: email.contact_email,
          subject,
          html: body,
          from_name: 'Aureum VII Trading Team',
          reply_to: 'info@aureum7trading.com'
        }
      });

      if (sendError) throw sendError;

      // Update backlink
      await supabase
        .from('backlinks')
        .update({
          outreach_status: 'follow_up',
          follow_up_count: followUpNumber,
          last_outreach_date: new Date().toISOString(),
          next_follow_up_date: followUpNumber < automationRules[0].maxFollowUps 
            ? new Date(Date.now() + automationRules[0].followUpInterval * 24 * 60 * 60 * 1000).toISOString()
            : null,
          updated_at: new Date().toISOString()
        })
        .eq('id', email.backlink_id);

      toast.success(`Follow-up #${followUpNumber} sent to ${email.website_name}`);
      fetchScheduledEmails();
    } catch (error) {
      console.error('Error sending follow-up:', error);
      toast.error('Failed to send follow-up');
    }
  };

  const cancelFollowUp = async (email: ScheduledFollowUp) => {
    try {
      await supabase
        .from('backlinks')
        .update({ 
          next_follow_up_date: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', email.backlink_id);

      toast.success('Follow-up cancelled');
      fetchScheduledEmails();
    } catch (error) {
      console.error('Error cancelling follow-up:', error);
      toast.error('Failed to cancel follow-up');
    }
  };

  const runAutomatedFollowUps = async () => {
    const now = new Date();
    const dueFollowUps = scheduledEmails.filter(
      e => new Date(e.scheduled_for) <= now && e.status === 'pending'
    );

    if (dueFollowUps.length === 0) {
      toast.info('No follow-ups due at this time');
      return;
    }

    let sent = 0;
    for (const email of dueFollowUps) {
      try {
        await sendFollowUpNow(email);
        sent++;
        // Add delay between emails to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 1000));
      } catch (error) {
        console.error(`Failed to send to ${email.website_name}:`, error);
      }
    }

    toast.success(`Sent ${sent} automated follow-up emails`);
  };

  const generateFollowUpBody = (backlink: any, followUpNumber: number): string => {
    const templates = {
      1: `<div style="font-family: Arial, sans-serif; line-height: 1.6;">
<p>Hi ${backlink.contact_name || 'there'},</p>

<p>I wanted to follow up on my previous email regarding a potential partnership between ${backlink.website_name} and Aureum VII Trading.</p>

<p>We offer exclusive content collaborations including:</p>
<ul>
  <li>Product review opportunities with complimentary units</li>
  <li>High-resolution imagery and video assets</li>
  <li>Expert quotes for your articles</li>
</ul>

<p>Would you have 15 minutes this week to discuss?</p>

<p>Best regards,<br>
Aureum VII Trading<br>
www.aureum7trading.com</p>
</div>`,
      2: `<div style="font-family: Arial, sans-serif; line-height: 1.6;">
<p>Hi ${backlink.contact_name || 'there'},</p>

<p>I understand you're busy, so I'll keep this brief.</p>

<p>I've attached our latest smart toilet collection guide that might be valuable for your ${backlink.category} audience. No strings attached—feel free to reference it in future content.</p>

<p>If you'd ever like to explore a collaboration, I'm just a reply away.</p>

<p>Wishing you success,<br>
Aureum VII Trading Team</p>
</div>`,
      3: `<div style="font-family: Arial, sans-serif; line-height: 1.6;">
<p>Hi ${backlink.contact_name || 'there'},</p>

<p>This will be my last follow-up. I don't want to clutter your inbox.</p>

<p>If timing isn't right for a partnership now, I completely understand. Should your needs change in the future, our offer stands:</p>
<ul>
  <li>Free product reviews</li>
  <li>Guest content opportunities</li>
  <li>Exclusive industry insights</li>
</ul>

<p>Thanks for your time, and best of luck with ${backlink.website_name}!</p>

<p>Regards,<br>
Aureum VII Trading</p>
</div>`,
    };

    return templates[followUpNumber as keyof typeof templates] || templates[1];
  };

  const toggleAutomation = (ruleId: string) => {
    setAutomationRules(prev => prev.map(rule => 
      rule.id === ruleId ? { ...rule, enabled: !rule.enabled } : rule
    ));
    toast.success('Automation settings updated');
  };

  const getDueStatus = (scheduledFor: string) => {
    const scheduled = new Date(scheduledFor);
    const now = new Date();
    const diffDays = Math.ceil((scheduled.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return { text: 'Overdue', variant: 'destructive' as const };
    if (diffDays === 0) return { text: 'Due Today', variant: 'default' as const };
    if (diffDays === 1) return { text: 'Tomorrow', variant: 'secondary' as const };
    return { text: `In ${diffDays} days`, variant: 'outline' as const };
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="text-center text-muted-foreground">Loading scheduled outreach...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Clock className="h-6 w-6" />
            Scheduled Outreach Automation
          </h2>
          <p className="text-muted-foreground">Automated follow-up email sequences</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchScheduledEmails}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={runAutomatedFollowUps} disabled={scheduledEmails.length === 0}>
            <Zap className="h-4 w-4 mr-2" />
            Run Due Follow-ups
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{scheduledEmails.length}</div>
            <p className="text-xs text-muted-foreground">Scheduled Follow-ups</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-yellow-500">
              {scheduledEmails.filter(e => new Date(e.scheduled_for) <= new Date()).length}
            </div>
            <p className="text-xs text-muted-foreground">Due Now</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-blue-500">
              {scheduledEmails.filter(e => {
                const diff = Math.ceil((new Date(e.scheduled_for).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                return diff > 0 && diff <= 7;
              }).length}
            </div>
            <p className="text-xs text-muted-foreground">Due This Week</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              {automationRules[0].enabled ? (
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              ) : (
                <AlertCircle className="h-5 w-5 text-yellow-500" />
              )}
              <div>
                <div className="text-lg font-bold">
                  {automationRules[0].enabled ? 'Active' : 'Paused'}
                </div>
                <p className="text-xs text-muted-foreground">Automation</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Automation Rules */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings2 className="h-5 w-5" />
            Automation Rules
          </CardTitle>
          <CardDescription>Configure follow-up intervals and sequences</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {automationRules.map((rule) => (
            <div key={rule.id} className="flex items-center justify-between p-4 border rounded-lg">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <Switch
                    checked={rule.enabled}
                    onCheckedChange={() => toggleAutomation(rule.id)}
                  />
                  <div>
                    <p className="font-medium">{rule.name}</p>
                    <p className="text-sm text-muted-foreground">
                      Send up to {rule.maxFollowUps} follow-ups, {rule.followUpInterval} days apart
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={rule.enabled ? 'default' : 'secondary'}>
                  {rule.enabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>
            </div>
          ))}

          {/* Add New Rule */}
          <div className="grid md:grid-cols-4 gap-4 p-4 border rounded-lg border-dashed">
            <div>
              <Label>Rule Name</Label>
              <Input
                placeholder="e.g., Aggressive Follow-up"
                value={newRule.name}
                onChange={(e) => setNewRule(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div>
              <Label>Interval (days)</Label>
              <Select
                value={newRule.followUpInterval.toString()}
                onValueChange={(v) => setNewRule(prev => ({ ...prev, followUpInterval: parseInt(v) }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 day</SelectItem>
                  <SelectItem value="2">2 days</SelectItem>
                  <SelectItem value="3">3 days</SelectItem>
                  <SelectItem value="5">5 days</SelectItem>
                  <SelectItem value="7">7 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Max Follow-ups</Label>
              <Select
                value={newRule.maxFollowUps.toString()}
                onValueChange={(v) => setNewRule(prev => ({ ...prev, maxFollowUps: parseInt(v) }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1</SelectItem>
                  <SelectItem value="2">2</SelectItem>
                  <SelectItem value="3">3</SelectItem>
                  <SelectItem value="5">5</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button className="w-full" variant="outline" disabled={!newRule.name}>
                <Plus className="h-4 w-4 mr-2" />
                Add Rule
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Scheduled Emails Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Pending Follow-ups</CardTitle>
          <CardDescription>Scheduled emails awaiting delivery</CardDescription>
        </CardHeader>
        <CardContent>
          {scheduledEmails.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Calendar className="h-12 w-12 mx-auto mb-4 opacity-20" />
              <p>No follow-ups scheduled</p>
              <p className="text-sm">Send initial outreach emails to start scheduling</p>
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Website</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Follow-up #</TableHead>
                    <TableHead>Scheduled For</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {scheduledEmails.map((email) => {
                    const dueStatus = getDueStatus(email.scheduled_for);
                    return (
                      <TableRow key={email.id}>
                        <TableCell className="font-medium">{email.website_name}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {email.contact_email || '-'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">#{email.follow_up_number}</Badge>
                        </TableCell>
                        <TableCell>
                          {new Date(email.scheduled_for).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant={dueStatus.variant}>{dueStatus.text}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => sendFollowUpNow(email)}
                            >
                              <Play className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => cancelFollowUp(email)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
